import React, { useState, useRef, useEffect } from 'react';
import { BrokerPosition, BrokerOrder, useActiveBrokerBalance, useBrokerStore } from '../store/brokerStore';
import { calculatePnL, roundToTick } from '../config/instruments';
import { usePreferences } from '../hooks/usePreferences';
import { useDraggable } from '../hooks/useDraggable';
import { useToastStore } from '../store/toastStore';
import { useAutoBeStore } from '../store/autoBeStore';
import AutoBeBadge from './AutoBeBadge';
import { RestrainBadge, useRestrainFor } from './Restrain';
import { restrainLockMessage } from '../store/restrainStore';
import './PositionControlsOverlay.css';

export interface PositionControlsOverlayProps {
  position: BrokerPosition;
  currentPrice: number;
  orders: BrokerOrder[];
  symbol: string;
  onFlatten: () => void;
  onModifyOrder: (orderId: number, newPrice: number, isStopPrice: boolean, brokerId?: string, accountId?: number, skipDebounce?: boolean) => void;
  onModifyOrderQuantity: (orderId: number, newQuantity: number) => void;
  onReplaceBracket: (params: {
    tpOrderId?: number;
    slOrderId?: number;
    symbol: string;
    action: 'Buy' | 'Sell';
    newQuantity: number;
    tpPrice?: number;
    slPrice?: number;
  }) => Promise<{ success: boolean; error?: string }>;
  onPartialExit: (quantity: number) => void;
  dcaMode?: boolean;
  onDcaToggle?: () => void;
  /** Start a draggable PENDING take-profit bracket line for this position. */
  onAddTP?: () => void;
  /** Start a draggable PENDING stop-loss bracket line for this position. */
  onAddSL?: () => void;
}

// Format elapsed milliseconds into a compact "time in position" string.
// < 60s -> "12s"; < 1h -> "5m 12s"; else "2h 5m"
const formatTimeInPosition = (elapsedMs: number): string => {
  const totalSec = Math.floor(elapsedMs / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}m ${s}s`;
  }
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${h}h ${m}m`;
};

const PositionControlsOverlay: React.FC<PositionControlsOverlayProps> = ({
  position,
  currentPrice,
  orders,
  symbol,
  onFlatten,
  onModifyOrder,
  onModifyOrderQuantity,
  onReplaceBracket,
  onPartialExit,
  dcaMode = false,
  onDcaToggle,
  onAddTP,
  onAddSL,
}) => {
  const isLong = position.netPos > 0;
  const quantity = Math.abs(position.netPos);
  const entryPrice = position.netPrice;
  // Calculate real-time PnL using position symbol
  const pnl = calculatePnL(entryPrice, currentPrice, position.netPos, position.symbol || symbol).dollars;
  const isInProfit = pnl > 0;
  const isInLoss = pnl < 0;

  const { realizedPnL } = useActiveBrokerBalance();
  const dayPnL = realizedPnL + pnl;

  // "Time in position" timer - anchored to when the position first opened.
  // Keyed by the same symbol the store uses (position.symbol || symbol).
  const openedAt = useBrokerStore((s) => s.positionOpenedAt[position.symbol || symbol]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!openedAt) return;
    setNow(Date.now()); // refresh immediately on (re)anchor
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [openedAt]);
  const elapsedMs = openedAt ? Math.max(0, now - openedAt) : 0;
  const timeInPosition = openedAt ? formatTimeInPosition(elapsedMs) : null;

  // Find ALL SL and TP orders (there can be multiple after splitting)
  const slOrders = orders.filter(order => {
    const isStopOrder = order.ordType?.toLowerCase().includes('stop');
    const isWorkingOrder = order.ordStatus === 'Working' || order.ordStatus === 'Accepted' || order.ordStatus === 'Pending';
    const isOppositeAction = isLong ? order.action === 'Sell' : order.action === 'Buy';
    return isStopOrder && isWorkingOrder && isOppositeAction && order.stopPrice;
  });

  const tpOrders = orders.filter(order => {
    const isLimitOrder = order.ordType?.toLowerCase().includes('limit') && !order.ordType?.toLowerCase().includes('stop');
    const isWorkingOrder = order.ordStatus === 'Working' || order.ordStatus === 'Accepted' || order.ordStatus === 'Pending';
    const isOppositeAction = isLong ? order.action === 'Sell' : order.action === 'Buy';
    return isLimitOrder && isWorkingOrder && isOppositeAction && order.price;
  });

  // For backward compatibility - use first order when single order needed
  const slOrder = slOrders[0];
  const tpOrder = tpOrders[0];

  // Auto B/E is ONE shared setting executed by the engine (utt-engine
  // auto_be), never by this component: when it ran here, every open tab and
  // device moved stops independently, and an idle phone tab moved a live SL.
  // The toggle only reads and writes the shared setting.
  const autoBE = useAutoBeStore(state => state.enabled);
  const setAutoBE = useAutoBeStore(state => state.setEnabled);
  const autoBETriggerPct = useAutoBeStore(state => state.triggerPct);
  const isHedgeActive = useBrokerStore(state => state.isHedgeCopyModeActive());
  // Turning it ON is refused while hedge mode is on (the engine would not act
  // anyway); turning it OFF is always allowed.
  const autoBEDisabled = isHedgeActive && !autoBE;

  // Restrain: size is frozen for this position. Adding (DCA) is greyed out;
  // flatten, TP/SL, BE/PS and sync stay live. Turning DCA OFF stays allowed.
  const { entry: restrain } = useRestrainFor({ ...position, fallbackSymbol: symbol });
  const restrainMsg = restrain ? restrainLockMessage(restrain) : null;
  const dcaLocked = !!restrainMsg && !dcaMode;

  // Use draggable hook for overlay positioning
  // Separate positions for mobile vs desktop due to different screen sizes
  const overlayRef = useRef<HTMLDivElement>(null);
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
  const { position: overlayPosition, isDragging, dragHandlers } = useDraggable(overlayRef, {
    defaultPosition: { x: 10, y: 50 },
    mobileDefaultPosition: { x: 8, y: 100 }, // Top area on mobile - safe visible area
    preferenceKey: 'positionControlsOverlay',
    constrainToViewport: true,
    excludeSelectors: ['button', 'label']
  });

  // Debug: Force visible position on mobile if it's off-screen
  const safePosition = isMobileView ? {
    x: Math.min(Math.max(overlayPosition.x, 0), window.innerWidth - 200),
    y: Math.min(Math.max(overlayPosition.y, 60), window.innerHeight - 150)
  } : overlayPosition;

  // Toast helper
  const addToast = useToastStore(state => state.addToast);

  // Handlers
  const handleBreakEven = async () => {
    const newPrice = roundToTick(entryPrice, symbol);

    if (slOrders.length > 0) {
      // Move existing SL orders to break-even
      slOrders.forEach(order => {
        if (order.stopPrice) {
          onModifyOrder(order.id, newPrice, true, undefined, undefined, true);
        }
      });
      const count = slOrders.length;
      addToast({
        type: 'success',
        message: count > 1
          ? `${count} SL orders moved to break-even @ ${newPrice.toFixed(2)}`
          : `SL moved to break-even @ ${newPrice.toFixed(2)}`
      });
    } else {
      // No SL exists - create one at break-even using replaceBracket
      const isLong = position.netPos > 0;
      const exitAction = isLong ? 'Sell' : 'Buy';
      const result = await onReplaceBracket({
        tpOrderId: tpOrders[0]?.id,  // Keep existing TP if any
        symbol,
        action: exitAction,
        newQuantity: Math.abs(position.netPos),
        tpPrice: tpOrders[0]?.price,  // Preserve TP price
        slPrice: newPrice,             // New SL at break-even
      });
      if (result.success) {
        addToast({ type: 'success', message: `SL created at break-even @ ${newPrice.toFixed(2)}` });
      } else {
        addToast({ type: 'error', message: `Failed to create SL: ${result.error}` });
      }
    }
  };

  const handlePlaySafe = () => {
    if (tpOrders.length === 0) return;

    // Use dynamic symbol instead of hardcoded
    const newPrice = roundToTick(entryPrice, symbol);

    // Move ALL TP orders to entry (skipDebounce=true for fast button response)
    tpOrders.forEach(order => {
      if (order.price) {
        onModifyOrder(order.id, newPrice, false, undefined, undefined, true);  // skipDebounce=true
      }
    });

    const count = tpOrders.length;
    addToast({
      type: 'success',
      message: count > 1
        ? `${count} TP orders moved to entry @ ${newPrice.toFixed(2)}`
        : `TP moved to entry @ ${newPrice.toFixed(2)}`
    });
  };

  // Get OCO settings from preferences for recreating missing orders
  const { preferences } = usePreferences();
  const useOCO = preferences?.tradingPanel?.useOCO ?? true;
  const ocoTPOffset = preferences?.tradingPanel?.ocoTPOffset ?? 5;
  const ocoSLOffset = preferences?.tradingPanel?.ocoSLOffset ?? 50;

  // Check if TP/SL quantities are out of sync with position
  // Also consider "out of sync" if either TP or SL is missing (when OCO is enabled)
  const tpQty = tpOrder?.orderQty ?? 0;
  const slQty = slOrder?.orderQty ?? 0;
  const hasQtyMismatch = (tpOrder && tpQty !== quantity) || (slOrder && slQty !== quantity);
  const hasMissingOrder = useOCO && (!tpOrder || !slOrder);
  const isOutOfSync = hasQtyMismatch || hasMissingOrder;

  const handleSyncQty = async () => {
    if (!tpOrder && !slOrder && !useOCO) {
      addToast({ type: 'warning', message: 'No TP/SL orders to sync' });
      return;
    }

    // Build description of what we're syncing
    const syncActions: string[] = [];
    if (!tpOrder) syncActions.push('recreate TP');
    if (!slOrder) syncActions.push('recreate SL');
    if (tpOrder && tpQty !== quantity) syncActions.push(`TP qty ${tpQty}→${quantity}`);
    if (slOrder && slQty !== quantity) syncActions.push(`SL qty ${slQty}→${quantity}`);

    addToast({ type: 'info', message: `Syncing: ${syncActions.join(', ')}...` });

    // Use replaceBracket to cancel and replace orders atomically
    // This works around Tradovate's limitation that Limit orders in brackets can't be modified
    const exitAction = isLong ? 'Sell' : 'Buy';

    // If an order is missing, calculate the price from ENTRY PRICE + offset (not current price)
    // For a long position: TP is above entry, SL is below entry
    // For a short position: TP is below entry, SL is above entry
    const calculatedTpPrice = tpOrder?.price ?? (useOCO ? (isLong ? entryPrice + ocoTPOffset : entryPrice - ocoTPOffset) : undefined);
    const calculatedSlPrice = slOrder?.stopPrice ?? (useOCO ? (isLong ? entryPrice - ocoSLOffset : entryPrice + ocoSLOffset) : undefined);

    console.log('[SYNC] handleSyncQty called:', {
      tpOrderId: tpOrder?.id,
      slOrderId: slOrder?.id,
      symbol,
      action: exitAction,
      newQuantity: quantity,
      tpPrice: calculatedTpPrice,
      slPrice: calculatedSlPrice,
      useOCO,
      ocoTPOffset,
      ocoSLOffset,
      entryPrice,
      isLong,
    });

    const result = await onReplaceBracket({
      tpOrderId: tpOrder?.id,
      slOrderId: slOrder?.id,
      symbol,
      action: exitAction,
      newQuantity: quantity,
      tpPrice: calculatedTpPrice,
      slPrice: calculatedSlPrice,
    });

    console.log('[SYNC] replaceBracket result:', result);

    if (result.success) {
      addToast({ type: 'success', message: `TP/SL synced to qty ${quantity}` });
    } else {
      addToast({ type: 'error', message: `Sync failed: ${result.error}` });
    }
  };

  const breakEvenDisabled = !isInProfit;
  const playSafeDisabled = !isInLoss || tpOrders.length === 0;

  return (
    <div
      ref={overlayRef}
      className={`pco-strip ${isLong ? 'long' : 'short'} ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${safePosition.x}px`,
        top: `${safePosition.y}px`,
      }}
      {...dragHandlers}
    >
      <span className="pco-label">{isLong ? 'L' : 'S'}</span>
      <span className="pco-qty">x{quantity}</span>
      <span className={`pco-pnl ${pnl >= 0 ? 'pos' : 'neg'}`}>
        {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}
      </span>
      <span className={`pco-day-pnl ${dayPnL >= 0 ? 'pos' : 'neg'}`}>
        D:{dayPnL >= 0 ? '+' : ''}{dayPnL.toFixed(0)}
      </span>
      {timeInPosition && (
        <span className="pco-timer" title="Time in position">
          ⏱ {timeInPosition}
        </span>
      )}
      {/* Mobile-only flex line break: forces the action buttons onto a 2nd row
          so the strip isn't too wide on phones. Hidden (display:none) on desktop. */}
      <div className="pco-row-break" aria-hidden="true" />
      {onAddTP && (
        <button
          className="pco-btn pco-btn-tp"
          onClick={onAddTP}
          title="Add / edit take-profit (drag then confirm)"
        >
          TP
        </button>
      )}
      {onAddSL && (
        <button
          className="pco-btn pco-btn-sl"
          onClick={onAddSL}
          title="Add / edit stop-loss (drag then confirm)"
        >
          SL
        </button>
      )}
      {onDcaToggle && (
        <button
          className={`pco-btn ${dcaMode ? 'on' : ''}${dcaLocked ? ' off restrain-locked' : ''}`}
          onClick={onDcaToggle}
          disabled={dcaLocked}
          title={dcaLocked ? restrainMsg! : dcaMode ? 'Exit DCA mode' : 'DCA Calculator'}
        >
          DCA
        </button>
      )}
      <button
        className={`pco-btn ${breakEvenDisabled ? 'off' : ''}`}
        onClick={handleBreakEven}
        disabled={breakEvenDisabled}
        title="Break Even"
      >
        BE
      </button>
      <button
        className={`pco-btn ${playSafeDisabled ? 'off' : ''}`}
        onClick={handlePlaySafe}
        disabled={playSafeDisabled}
        title="Play Safe"
      >
        PS
      </button>
      <label
        className={`pco-btn toggle ${autoBEDisabled ? 'off' : ''} ${autoBE ? 'on' : ''}`}
        title={isHedgeActive
          ? 'Auto B/E does not act while hedge mode is on'
          : `Auto B/E (all devices): the engine moves the SL to breakeven at ${autoBETriggerPct}% of TP`}
      >
        <input
          type="checkbox"
          checked={autoBE}
          disabled={autoBEDisabled}
          onChange={(e) => { void setAutoBE(e.target.checked); }}
        />
        ABE
      </label>
      <AutoBeBadge position={position} orders={orders} />
      <RestrainBadge position={position} fallbackSymbol={symbol} />
      <button
        className={`pco-btn sync ${isOutOfSync ? 'warn' : 'off'}`}
        onClick={handleSyncQty}
        disabled={!isOutOfSync}
        title={isOutOfSync ? `Sync TP/SL qty to ${quantity}` : 'TP/SL in sync'}
      >
        ⟳
      </button>
      <button className="pco-btn flat" onClick={onFlatten} title="Flatten">
        X
      </button>
    </div>
  );
};

export default PositionControlsOverlay;
